import { currencies, chains } from "@/cuties/blockchain/matic/web3Utils";
import type { ConfirmedGasFormData, ConfirmGasPriceParams, Web3Provider } from "@/cuties/blockchain/matic/Web3Provider";
import type Web3 from "web3";
import type { PersonalMessageSignature, WalletProviderKind } from "@/components/LoginManager/TypeDefs";
import type { EthereumConfig } from "@/cuties/blockchain/ethereum/EthereumContractAddresses";
import type { Blockchain } from "@/cuties/model/pet/BlockchainId";
import { BlockchainNetworkError } from "@/cuties/blockchain/BlockchainService";
import { sleep } from "@/app/cuties/utils/utils";
import CutiesApiFaucet from "@/app/cuties/blockchain/CutiesApiFaucet";
import type EthereumProvider from "@walletconnect/ethereum-provider/dist/types/EthereumProvider";
import { ShowLoader } from "@/common/decorators/ShowLoader";

export interface Params {
    kind: WalletProviderKind;
    ethereumProvider: EthereumProvider;
    config: EthereumConfig;
}

export default class Web3MetamaskProvider implements Web3Provider {
    private readonly whenWeb3: Promise<Web3>;
    private accounts: string[] = [];

    private constructor(readonly blockchain: Blockchain, readonly params: Params) {
        this.params = params;
        this.whenWeb3 = CutiesApiFaucet.getWeb3().then((Web3) => new Web3(params.ethereumProvider));

        this.params.ethereumProvider.on("accountsChanged", (accounts) => {
            this.accounts = accounts;
        });

        // wallet connect event when session deleted or removed from prone
        this.params.ethereumProvider.on("session_delete", () => {
            location.reload(); // user deleted session from phone or got dissconected - quick remove provider is f5
            // we can call params.ethereumProvider.connect() to reconnct, but in case reject we cant clean provider
        });
    }

    @ShowLoader
    public static async create(blockchain: Blockchain, params: Params): Promise<Web3MetamaskProvider> {
        const provider = new Web3MetamaskProvider(blockchain, params);

        provider.accounts = await params.ethereumProvider.request({ method: "eth_requestAccounts" });

        // some wallets hide network settings from a user
        if (!(await provider.isExpectedNetwork())) {
            await provider.switchNetwork();
            await sleep(200); // add delay because wallets reject fast calls
        }

        return provider;
    }

    public static chainIdToName(chainId: number): string {
        return chains[chainId] || "";
    }

    public async getWalletAddress(): Promise<string> {
        if (this.accounts.length > 0) {
            return this.accounts[0];
        } else {
            throw new Error("No Wallet Addresses Are Available");
        }
    }

    public async getWeb3(): Promise<Web3> {
        return this.whenWeb3;
    }

    public async checkReadyForTransaction(): Promise<void> {
        return this.checkSelectedNetwork();
    }

    public async signPersonalMessage(termsText: string): Promise<PersonalMessageSignature> {
        const web3 = await this.getWeb3();
        const hexed = web3.utils.utf8ToHex(termsText);
        const address = await this.getWalletAddress();

        return this.params.ethereumProvider.request({
            method: "personal_sign",
            params: [hexed, address],
        });
    }

    public async confirmGasPrice({ sendOptions, gasPolicy }: ConfirmGasPriceParams): Promise<ConfirmedGasFormData> {
        switch (gasPolicy.type) {
            case "legacy":
                return { gas: sendOptions.gas ?? 0, gasPrice: gasPolicy.gasPrices.Normal };
            case "eip1559":
                return {
                    gas: sendOptions.gas ?? 0,
                    maxPriorityFeePerGas: gasPolicy.gasPrices.Normal,
                    maxFeePerGas: Number(gasPolicy.baseFee) + Number(gasPolicy.gasPrices.Normal),
                };
        }
    }

    public async isExpectedNetwork(): Promise<boolean> {
        const web3 = await this.whenWeb3;

        const whenNetworkId = web3.eth.net.getId();
        const whenTimeout = new Promise((resolve, reject) => setTimeout(() => reject("Error on checking network id"), 10000));
        const networkId = await Promise.race([whenNetworkId, whenTimeout]);
        return this.params.config.networkId === networkId;
    }

    /**
     * Add blockchain network to the user wallet
     * @see https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
     */
    public async addNetwork(): Promise<unknown> {
        const web3 = await this.getWeb3();
        const { networkId, etherscanUrl, nodeUrl } = this.params.config;
        const symbol = currencies[this.blockchain] ?? "";

        console.log(networkId, web3.utils.toHex(networkId), etherscanUrl, nodeUrl, symbol);
        return this.params.ethereumProvider.request({
            method: "wallet_addEthereumChain",
            params: [
                {
                    chainId: web3.utils.toHex(networkId),
                    chainName: Web3MetamaskProvider.chainIdToName(networkId),
                    nativeCurrency: { name: symbol, symbol: symbol, decimals: 18 },
                    rpcUrls: [nodeUrl + "/"], // must end with /
                    blockExplorerUrls: [etherscanUrl + "/"],
                },
            ],
        });
    }

    /**
     * Switch blockchain network in the user wallet
     * @see https://docs.metamask.io/guide/rpc-api.html#wallet-switchethereumchain
     */
    public async switchNetwork(): Promise<unknown> {
        const web3 = await this.getWeb3();
        try {
            return await this.params.ethereumProvider.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: web3.utils.toHex(this.params.config.networkId) }],
            });
        } catch (e) {
            // network not present in metamask
            if (e.code === 4902) return await this.addNetwork();
            throw e;
        }
    }

    private async checkSelectedNetwork(): Promise<void> {
        const web3 = await this.whenWeb3;
        const networkType = await web3.eth.net.getNetworkType();
        const networkId = await web3.eth.net.getId();

        const neededId = this.params.config.networkId;
        if (networkId != neededId) {
            const neededComment = Web3MetamaskProvider.chainIdToName(neededId);
            throw new BlockchainNetworkError(
                `${networkId} (${Web3MetamaskProvider.chainIdToName(networkId) || " (" + networkType + "))"}`,
                `${neededId} (${neededComment})`
            );
        }
    }
}
